/**
 * Barra de filtros reutilizable (fechas, selects, busqueda, atajos).
 *
 * Uso:
 *   <FilterBar
 *     desde={desde} hasta={hasta}
 *     onDesde={setDesde} onHasta={setHasta}
 *     onLimpiar={limpiar}
 *     resumen="12 resultados · Total ₡1.200"
 *   >
 *     <select .../>
 *   </FilterBar>
 */
import { hoy } from '../utils/format.js';

/**
 * Corre una fecha de calendario (YYYY-MM-DD) una cantidad de dias.
 * Es aritmetica de calendario pura: no pasa por la zona horaria.
 */
function desplazar(iso, dias) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + dias)).toISOString().slice(0, 10);
}

export default function FilterBar({
  children,
  desde,
  hasta,
  onDesde,
  onHasta,
  busqueda,
  onBusqueda,
  placeholderBusqueda = 'Buscar…',
  onLimpiar,
  resumen,
  showFechas = true,
  showBusqueda = false,
  atajos = true,
}) {
  // Los atajos se calculan con la fecha de Costa Rica. Antes usaban
  // toISOString(), que da la fecha en UTC: de noche, cuando en Costa Rica
  // todavia es hoy, en UTC ya es mañana, y "Hoy" filtraba el dia siguiente.
  const setRango = (dias) => {
    if (!onDesde || !onHasta) return;
    const fin = hoy();
    let ini = fin;
    if (dias === 'mes') ini = `${fin.slice(0, 8)}01`;
    else if (dias !== 'hoy') ini = desplazar(fin, -(Number(dias) || 30));
    onDesde(ini);
    onHasta(fin);
  };

  const hayFiltros =
    (desde && String(desde)) ||
    (hasta && String(hasta)) ||
    (busqueda && String(busqueda).trim()) ||
    false;

  return (
    <div className="card" style={{ padding: 14, marginBottom: 16 }}>
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          alignItems: 'flex-end',
        }}
      >
        {showBusqueda && (
          <div className="field" style={{ margin: 0, flex: '1 1 180px', minWidth: 160 }}>
            <label>Buscar</label>
            <input
              className="input"
              value={busqueda || ''}
              onChange={(e) => onBusqueda?.(e.target.value)}
              placeholder={placeholderBusqueda}
            />
          </div>
        )}

        {showFechas && (
          <>
            <div className="field" style={{ margin: 0 }}>
              <label>Desde</label>
              <input
                className="input"
                type="date"
                value={desde || ''}
                onChange={(e) => onDesde?.(e.target.value)}
              />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>Hasta</label>
              <input
                className="input"
                type="date"
                value={hasta || ''}
                onChange={(e) => onHasta?.(e.target.value)}
              />
            </div>
          </>
        )}

        {children}

        {atajos && showFechas && (
          <div className="flex gap-sm" style={{ flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRango('hoy')}>
              Hoy
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRango(7)}>
              7 dias
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRango(30)}>
              30 dias
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRango('mes')}>
              Este mes
            </button>
          </div>
        )}

        {onLimpiar && (
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={onLimpiar}
            disabled={!hayFiltros && !children}
            title="Limpiar filtros"
          >
            Limpiar
          </button>
        )}
      </div>

      {resumen != null && resumen !== '' && (
        <div className="muted" style={{ fontSize: 13, marginTop: 10 }}>
          {resumen}
        </div>
      )}
    </div>
  );
}
